// src/containers/TransactionPage/TransactionPage.js
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import {
  denormalisedResponseEntities,
  ensureBooking,
  ensureListing,
  ensureTransaction,
} from '../../util/data';
import { dateFromAPIToLocalNoon, dateFromLocalToAPI, getDateForEmail } from '../../util/dates';
import { createSlug } from '../../util/urlHelpers';
import { txIsInitiatePayment } from '../../util/customTransaction/mainRentalTransaction';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import {
  NamedRedirect,
  TransactionPanel,
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
} from '../../components';
import { TopbarContainer } from '../../containers';
import {
  rescheduleSale,
  acceptSale,
  declineSale,
  loadData,
  setInitialValues,
  sendMessage,
  sendReview,
  fetchMoreMessages,
  fetchTransactionLineItems,
  transit,
  fetchExtendTransactionLineItem,
  timeSlotsRequest,
  retryPayment,
  renewBond,
  fetchReofferTransactionLineItem,
  changeAvailabilityExceptionCurrentBooking,
  createHoldingSlotTx,
  extendRescheduleSale,
  fetchTransaction,
  declineHoldingSlotTx,
} from './TransactionPage.duck';
import css from './TransactionPage.css';
import { exTxIsPendingPayment } from '../../util/customTransaction/extendRentalTransaction';
import {
  initiateOrder,
  speculateTransaction,
  transit as extendTransit,
} from '../CheckoutPage/ExtendCheckoutPage.duck';
import moment from 'moment';
import { convertMoneyToNumber } from '../../util/currency';

const PROVIDER = 'provider';
const CUSTOMER = 'customer';

export const TransactionPageComponent = props => {
  const gtmPurchaseFired = useRef(false);
  const {
    currentUser,
    transaction,
    transactionRole,
    onFetchTransaction,
    // ... other props from original file
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    fetchMessagesError,
    fetchMessagesInProgress,
    totalMessagePages,
    oldestMessagePageFetched,
    fetchTransactionError,
    history,
    intl,
    messages,
    onManageDisableScrolling,
    onSendMessage,
    onSendReview,
    onShowMoreMessages,
    params,
    scrollingDisabled,
    sendMessageError,
    sendMessageInProgress,
    sendReviewError,
    sendReviewInProgress,
    rescheduleInProgress,
    rescheduleSaleError,
    acceptInProgress,
    acceptSaleError,
    declineInProgress,
    declineSaleError,
    onRescheduleSale,
    onAcceptSale,
    onDeclineSale,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
    callSetInitialValues,
    onInitializeCardPaymentData,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    onTransit,
    transitError,
    transitInProgress,
    onFetchExtendTransactionLineItem,
    extendTransactions,
    lastExTransaction,
    onTimeSlotsRequest,
    retryPaymentInProgress,
    retryPaymentError,
    onRetryPayment,
    onRenewBond,
    renewBondInProgress,
    renewBondError,
    onFetchReofferTransactionLineItem,
    onChangeAvailabilityExceptionCurrentBooking,
    exceptionId,
    onCreateHoldingSlotTx,
    fetchSpeculatedTransaction,
    onInitiateOrder,
    onExtendTransit,
    speculatedTransaction: speculatedTransactionMaybe,
    speculateTransactionInProgress,
    speculateTransactionError,
    onExtendRescheduleSale,
    initiateOrderInProgress,
    onDeclineHoldingSlotTx,
  } = props;

  const currentTransaction = ensureTransaction(transaction);

  useEffect(() => {
    const isCustomerViewing = transactionRole === CUSTOMER;
    const isAccepted = currentTransaction?.attributes?.lastTransition === 'transition/accept';
    const isPending =
      currentTransaction?.attributes?.lastTransition === 'transition/request-payment';

    // --- GTM Purchase Event Logic ---
    if (isCustomerViewing && isAccepted && !gtmPurchaseFired.current) {
      gtmPurchaseFired.current = true; // Fire only once
      try {
        const listing = currentTransaction.listing;
        const lineItems = currentTransaction.attributes.lineItems;
        const publicData = listing.attributes?.publicData || {};
        const payinTotal = currentTransaction.attributes.payinTotal.amount / 100;
        const providerCommissionLineItem = lineItems.find(
          item => item.code === 'line-item/provider-commission'
        );
        const marketplaceCommission = providerCommissionLineItem
          ? Math.abs(providerCommissionLineItem.lineTotal.amount / 100)
          : 0;
        const booking = ensureBooking(currentTransaction.booking);
        const quantity = moment(booking.attributes.end).diff(
          moment(booking.attributes.start),
          'days'
        );

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: currentTransaction.id.uuid,
            value: payinTotal,
            marketplace_commission: marketplaceCommission,
            currency: 'AUD',
            items: [
              {
                item_id: listing.id.uuid,
                item_name: listing.attributes.title,
                item_brand: publicData.brand || null,
                item_category: publicData.category || null,
                item_variant: publicData.subCategory || null,
                quantity: quantity,
              },
            ],
          },
        });
      } catch (e) {
        console.error('GTM dataLayer push for purchase failed:', e);
      }
    }

    // --- Polling Logic ---
    let interval = null;
    if (isCustomerViewing && isPending) {
      interval = setInterval(() => {
        console.log('Polling for transaction update...');
        onFetchTransaction(currentTransaction.id, transactionRole);
      }, 20000); // 20 seconds
    }

    // Cleanup function to stop polling when the component unmounts or the transaction state changes
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [currentTransaction, transactionRole, onFetchTransaction, gtmPurchaseFired]);

  const currentListing = ensureListing(currentTransaction.listing);
  const isProviderRole = transactionRole === PROVIDER;
  const isCustomerRole = transactionRole === CUSTOMER;

  const redirectToCheckoutPageWithInitialValues = (
    initialValues,
    listing,
    page = 'CheckoutPage'
  ) => {
    const routes = routeConfiguration();
    const { setInitialValues } = findRouteByRouteName(page, routes);
    callSetInitialValues(setInitialValues, initialValues);
    onInitializeCardPaymentData();
    history.push(
      createResourceLocatorString(
        page,
        routes,
        { id: currentListing.id.uuid, slug: createSlug(currentListing.attributes.title) },
        {}
      )
    );
  };

  if (
    txIsInitiatePayment(currentTransaction) &&
    isCustomerRole &&
    currentTransaction.attributes.lineItems
  ) {
    const currentBooking = ensureBooking(currentTransaction.booking);
    const initialValues = {
      listing: currentListing,
      transaction: currentTransaction,
      bookingData: {},
      bookingDates: {
        bookingStart: dateFromAPIToLocalNoon(currentBooking.attributes.start),
        bookingEnd: dateFromAPIToLocalNoon(currentBooking.attributes.end),
      },
    };
    redirectToCheckoutPageWithInitialValues(initialValues, currentListing);
  }

  if (
    transaction &&
    exTxIsPendingPayment(lastExTransaction) &&
    isCustomerRole &&
    lastExTransaction &&
    lastExTransaction.attributes &&
    lastExTransaction.attributes.lineItems
  ) {
    const initialValues = {
      listing: currentListing,
      transaction: lastExTransaction,
      bookingData: {
        currentTransaction: null,
        mainTransactionId: currentTransaction.id,
      },
    };
    redirectToCheckoutPageWithInitialValues(initialValues, currentListing, 'ExtendCheckoutPage');
  }

  const handleSubmitBookingRequest = async values => {
    const { bookingDates, ...bookingData } = values;
    const initialValues = {
      listing: currentListing,
      transaction: currentTransaction,
      bookingData,
      bookingDates: {
        bookingStart: bookingDates.startDate,
        bookingEnd: bookingDates.endDate,
      },
      confirmPaymentError: null,
    };
    redirectToCheckoutPageWithInitialValues(initialValues, currentListing);
  };

  const onCustomerExtendRental = async values => {
    const { transaction } = values;
    const { extendBookingData } =
      transaction && transaction.attributes ? transaction.attributes.protectedData : {};
    const { startDate, endDate, extendPrice, seats } = extendBookingData;
    const NOW = new Date();
    let bookingStartForAPI = dateFromLocalToAPI(startDate);
    if (moment(startDate).isBefore(NOW)) {
      bookingStartForAPI = dateFromLocalToAPI(NOW);
    }
    const bookingEndForAPI = dateFromLocalToAPI(endDate);
    const orderParams = {
      listingId: currentListing.id,
      bookingStart: bookingStartForAPI,
      bookingEnd: bookingEndForAPI,
      bookingDisplayStart: moment(startDate).toDate(),
      bookingDisplayEnd: moment(endDate).toDate(),
      extendPrice,
      seats,
    };
    await onDeclineHoldingSlotTx(currentTransaction.id.uuid);
    fetchSpeculatedTransaction(orderParams, transaction.id.uuid)
      .then(res => {
        const [speculatedTransactionMaybe] = denormalisedResponseEntities(res);
        const speculatedTransaction = ensureTransaction(speculatedTransactionMaybe, {}, null);
        const tx = speculatedTransaction ? speculatedTransaction : transaction;
        const bondReleaseTime = moment(tx.booking.attributes.displayEnd).add(60, 'hours');
        return onInitiateOrder({
          ...orderParams,
          bookingStart: tx.booking.attributes.start,
          bookingEnd: tx.booking.attributes.end,
          bookingDisplayStart: tx.booking.attributes.displayStart,
          bookingDisplayEnd: moment(tx.booking.attributes.displayEnd)
            .subtract(1, 'day')
            .endOf('day')
            .toDate(),
          protectedData: {
            mainTransactionId: currentTransaction.id.uuid,
            prevTransactionId: transaction.id.uuid,
            bondReleaseTime: getDateForEmail(bondReleaseTime),
            seats,
          },
        });
      })
      .then(() => {
        onFetchTransaction(currentTransaction.id, transactionRole);
      });
  };

  const onManualRetry = () => {
    const currentBooking = ensureBooking(currentTransaction.booking);
    const initialValues = {
      listing: currentListing,
      transaction: currentTransaction,
      bookingData: {},
      bookingDates: {
        bookingStart: dateFromAPIToLocalNoon(currentBooking.attributes.start),
        bookingEnd: dateFromAPIToLocalNoon(currentBooking.attributes.end),
      },
    };
    redirectToCheckoutPageWithInitialValues(initialValues, currentListing);
  };

  const deletedListingTitle = intl.formatMessage({
    id: 'TransactionPage.deletedListing',
  });
  const listingTitle = currentListing.attributes.deleted
    ? deletedListingTitle
    : currentListing.attributes.title;

  const isDataAvailable =
    currentUser &&
    currentTransaction.id &&
    currentTransaction.id.uuid === params.id &&
    currentTransaction.attributes.lineItems &&
    currentTransaction.customer &&
    currentTransaction.provider &&
    !fetchTransactionError;

  const isOwnSale =
    isDataAvailable &&
    isProviderRole &&
    currentUser.id.uuid === currentTransaction.provider.id.uuid;
  const isOwnOrder =
    isDataAvailable &&
    isCustomerRole &&
    currentUser.id.uuid === currentTransaction.customer.id.uuid;

  if (isDataAvailable && isProviderRole && !isOwnSale) {
    console.error('Tried to access a sale that was not owned by the current user');
    return <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />;
  } else if (isDataAvailable && isCustomerRole && !isOwnOrder) {
    console.error('Tried to access an order that was not owned by the current user');
    return <NamedRedirect name="InboxPage" params={{ tab: 'orders' }} />;
  }

  const detailsClassName = classNames(css.tabContent, css.tabContentVisible);

  const fetchErrorMessage = isCustomerRole
    ? 'TransactionPage.fetchOrderFailed'
    : 'TransactionPage.fetchSaleFailed';
  const loadingMessage = isCustomerRole
    ? 'TransactionPage.loadingOrderData'
    : 'TransactionPage.loadingSaleData';

  const loadingOrFailedFetching = fetchTransactionError ? (
    <p className={css.error}>
      <FormattedMessage id={`${fetchErrorMessage}`} />
    </p>
  ) : (
    <p className={css.loading}>
      <FormattedMessage id={`${loadingMessage}`} />
    </p>
  );

  const initialMessageFailed = !!(
    initialMessageFailedToTransaction &&
    currentTransaction.id &&
    initialMessageFailedToTransaction.uuid === currentTransaction.id.uuid
  );

  const panel = isDataAvailable ? (
    <TransactionPanel
      className={detailsClassName}
      currentUser={currentUser}
      transaction={currentTransaction}
      fetchMessagesInProgress={fetchMessagesInProgress}
      totalMessagePages={totalMessagePages}
      oldestMessagePageFetched={oldestMessagePageFetched}
      messages={messages}
      initialMessageFailed={initialMessageFailed}
      savePaymentMethodFailed={savePaymentMethodFailed}
      fetchMessagesError={fetchMessagesError}
      sendMessageInProgress={sendMessageInProgress}
      sendMessageError={sendMessageError}
      sendReviewInProgress={sendReviewInProgress}
      sendReviewError={sendReviewError}
      onManageDisableScrolling={onManageDisableScrolling}
      onShowMoreMessages={onShowMoreMessages}
      onSendMessage={onSendMessage}
      onSendReview={onSendReview}
      transactionRole={transactionRole}
      onRescheduleSale={onRescheduleSale}
      onAcceptSale={onAcceptSale}
      onDeclineSale={onDeclineSale}
      rescheduleInProgress={rescheduleInProgress}
      acceptInProgress={acceptInProgress}
      declineInProgress={declineInProgress}
      rescheduleSaleError={rescheduleSaleError}
      acceptSaleError={acceptSaleError}
      declineSaleError={declineSaleError}
      nextTransitions={processTransitions}
      onSubmitBookingRequest={handleSubmitBookingRequest}
      timeSlots={timeSlots}
      fetchTimeSlotsError={fetchTimeSlotsError}
      onFetchTransactionLineItems={onFetchTransactionLineItems}
      lineItems={lineItems}
      fetchLineItemsInProgress={fetchLineItemsInProgress}
      fetchLineItemsError={fetchLineItemsError}
      onTransit={onTransit}
      transitError={transitError}
      transitInProgress={transitInProgress}
      onFetchExtendTransactionLineItem={onFetchExtendTransactionLineItem}
      onCustomerExtendRental={onCustomerExtendRental}
      extendTransactions={extendTransactions}
      lastExTransaction={lastExTransaction}
      onTimeSlotsRequest={onTimeSlotsRequest}
      onManualRetry={onManualRetry}
      retryPaymentInProgress={retryPaymentInProgress}
      retryPaymentError={retryPaymentError}
      onRetryPayment={onRetryPayment}
      onRenewBond={onRenewBond}
      renewBondInProgress={renewBondInProgress}
      renewBondError={renewBondError}
      onFetchReofferTransactionLineItem={onFetchReofferTransactionLineItem}
      onChangeAvailabilityExceptionCurrentBooking={onChangeAvailabilityExceptionCurrentBooking}
      exceptionId={exceptionId}
      onCreateHoldingSlotTx={onCreateHoldingSlotTx}
      speculateTransactionInProgress={speculateTransactionInProgress}
      onExtendRescheduleSale={onExtendRescheduleSale}
      initiateOrderInProgress={initiateOrderInProgress}
      onDeclineHoldingSlotTx={onDeclineHoldingSlotTx}
    />
  ) : (
    loadingOrFailedFetching
  );

  return (
    <Page
      title={intl.formatMessage({ id: 'TransactionPage.title' }, { title: listingTitle })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.root}>{panel}</div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter className={css.footer}>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

TransactionPageComponent.defaultProps = {
  currentUser: null,
  fetchTransactionError: null,
  acceptSaleError: null,
  declineSaleError: null,
  transaction: null,
  fetchMessagesError: null,
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

const { bool, func, oneOf, shape, string, array, arrayOf, number } = PropTypes;

TransactionPageComponent.propTypes = {
  params: shape({ id: string }).isRequired,
  transactionRole: oneOf([PROVIDER, CUSTOMER]).isRequired,
  currentUser: propTypes.currentUser,
  fetchTransactionError: propTypes.error,
  rescheduleSaleError: propTypes.error,
  acceptSaleError: propTypes.error,
  declineSaleError: propTypes.error,
  rescheduleInProgress: bool.isRequired,
  acceptInProgress: bool.isRequired,
  declineInProgress: bool.isRequired,
  onRescheduleSale: func.isRequired,
  onAcceptSale: func.isRequired,
  onDeclineSale: func.isRequired,
  scrollingDisabled: bool.isRequired,
  fetchMessagesError: propTypes.error,
  totalMessagePages: number.isRequired,
  oldestMessagePageFetched: number.isRequired,
  messages: arrayOf(propTypes.message).isRequired,
  initialMessageFailedToTransaction: propTypes.uuid,
  savePaymentMethodFailed: bool,
  sendMessageInProgress: bool.isRequired,
  sendMessageError: propTypes.error,
  onShowMoreMessages: func.isRequired,
  onSendMessage: func.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),
  fetchTimeSlotsError: propTypes.error,
  callSetInitialValues: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  onFetchExtendTransactionLineItem: func.isRequired,
  onFetchReofferTransactionLineItem: func.isRequired,
  onTransit: func.isRequired,
  onTimeSlotsRequest: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    fetchTransactionError,
    rescheduleSaleError,
    acceptSaleError,
    declineSaleError,
    rescheduleInProgress,
    acceptInProgress,
    declineInProgress,
    transactionRef,
    fetchMessagesInProgress,
    fetchMessagesError,
    totalMessagePages,
    oldestMessagePageFetched,
    messages,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    sendMessageInProgress,
    sendMessageError,
    sendReviewInProgress,
    sendReviewError,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    transitError,
    transitInProgress,
    extendTransactionRef,
    retryPaymentInProgress,
    retryPaymentError,
    renewBondInProgress,
    renewBondError,
    exceptionId,
  } = state.TransactionPage;
  const { currentUser } = state.user;
  const {
    speculatedTransaction,
    speculateTransactionInProgress,
    speculateTransactionError,
    initiateOrderInProgress,
  } = state.ExtendCheckoutPage;

  const transactions = getMarketplaceEntities(state, transactionRef ? [transactionRef] : []);
  const transaction = transactions.length > 0 ? transactions[0] : null;
  const extendTransactions = getMarketplaceEntities(
    state,
    extendTransactionRef ? extendTransactionRef : []
  );

  const lastExTransaction = extendTransactions.find(transaction => {
    const { extendTransactionId } = transaction.attributes.protectedData;
    if (!extendTransactionId) {
      return true;
    }
    return false;
  });

  return {
    currentUser,
    fetchTransactionError,
    rescheduleSaleError,
    acceptSaleError,
    declineSaleError,
    rescheduleInProgress,
    acceptInProgress,
    declineInProgress,
    scrollingDisabled: isScrollingDisabled(state),
    transaction,
    fetchMessagesInProgress,
    fetchMessagesError,
    totalMessagePages,
    oldestMessagePageFetched,
    messages,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    sendMessageInProgress,
    sendMessageError,
    sendReviewInProgress,
    sendReviewError,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    transitError,
    transitInProgress,
    extendTransactions,
    lastExTransaction,
    retryPaymentInProgress,
    retryPaymentError,
    renewBondInProgress,
    renewBondError,
    exceptionId,
    speculatedTransaction,
    speculateTransactionInProgress,
    speculateTransactionError,
    initiateOrderInProgress,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onRescheduleSale: (transactionId, params) => dispatch(rescheduleSale(transactionId, params)),
    onAcceptSale: transactionId => dispatch(acceptSale(transactionId)),
    onDeclineSale: transactionId => dispatch(declineSale(transactionId)),
    onShowMoreMessages: txId => dispatch(fetchMoreMessages(txId)),
    onSendMessage: (txId, message) => dispatch(sendMessage(txId, message)),
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onSendReview: (role, tx, reviewRating, reviewContent) =>
      dispatch(sendReview(role, tx, reviewRating, reviewContent)),
    callSetInitialValues: (setInitialValues, values) => dispatch(setInitialValues(values)),
    onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
    onFetchTransactionLineItems: (bookingData, listingId, isOwnListing) =>
      dispatch(fetchTransactionLineItems(bookingData, listingId, isOwnListing)),
    onFetchExtendTransactionLineItem: (bookingData, listingId, isOwnListing) =>
      dispatch(fetchExtendTransactionLineItem(bookingData, listingId, isOwnListing)),
    onFetchReofferTransactionLineItem: (bookingData, listingId, isOwnListing) =>
      dispatch(fetchReofferTransactionLineItem(bookingData, listingId, isOwnListing)),
    onTransit: (transactionId, transition, params) =>
      dispatch(transit(transactionId, transition, params)),
    onTimeSlotsRequest: params => dispatch(timeSlotsRequest(params)),
    onRetryPayment: transactionId => dispatch(retryPayment({ transactionId })),
    onRenewBond: transactionId => dispatch(renewBond({ transactionId })),
    onChangeAvailabilityExceptionCurrentBooking: (transactionId, type) =>
      dispatch(changeAvailabilityExceptionCurrentBooking(transactionId, type)),
    onCreateHoldingSlotTx: ({ txId, params }) => dispatch(createHoldingSlotTx({ txId, params })),
    fetchSpeculatedTransaction: (params, transactionId) =>
      dispatch(speculateTransaction(params, transactionId)),
    onInitiateOrder: (params, transactionId) => dispatch(initiateOrder(params, transactionId)),
    onExtendTransit: (transactionId, transition, params) =>
      dispatch(extendTransit(transactionId, transition, params)),
    onExtendRescheduleSale: (extendTxId, params) =>
      dispatch(extendRescheduleSale(extendTxId, params)),
    onFetchTransaction: (extendTxId, txRole) => dispatch(fetchTransaction(extendTxId, txRole)),
    onDeclineHoldingSlotTx: txId => dispatch(declineHoldingSlotTx(txId)),
  };
};

const TransactionPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(TransactionPageComponent);

TransactionPage.loadData = loadData;
TransactionPage.setInitialValues = setInitialValues;

export default TransactionPage;
